import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ActivatedRoute } from '@angular/router';
import { SearchBoxComponent } from '@homeleite/common-components';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, takeUntil, map } from 'rxjs/operators';
import { Location } from '@angular/common';

/** Desktop breakpoint aligned with admin UX shell (persistent sidenav). */
const DESKTOP_MQ = '(min-width: 960px)';

@Component({
    selector: 'homeleite-layout-base',
    templateUrl: './layout-base.component.html',
    styleUrls: ['./layout-base.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LayoutBaseComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() fillPrimary = false;

    @Input() pageTitle: string = null;

    @Input() showSearch = false;

    @Input() searchPlaceholder = 'Pesquisar';

    @Input() showMenu = false;

    @Input() showBack = false;

    /** Quando false, oculta a toolbar verde (página com header próprio). */
    @Input() showActionBar = true;

    @Input() showLogout = false;

    @Input() homePath = '/';

    @Input() user: Observable<LayoutUser>;

    @Output() backClick = new EventEmitter<MouseEvent>();

    @Output() logoutClick = new EventEmitter<void>();

    @Output() searchChange = new EventEmitter<string>();

    @Output() menuOpened$ = new EventEmitter<boolean>();

    @ViewChild('menuContainer') menuContainer: ElementRef;

    private _searchBox: SearchBoxComponent;

    private _menuOpened = false;

    private searchSubject = new BehaviorSubject('');

    private destroy$ = new EventEmitter<boolean>();

    offline = false;

    isDesktop = false;

    isSearchOpened$: Observable<boolean>;

    @Input() onSideBackClick: () => void = () => this.closeMenu();

    constructor(
        private changeDetector: ChangeDetectorRef,
        private location: Location,
        private route: ActivatedRoute,
        private breakpointObserver: BreakpointObserver
    ) {
        this.searchSubject
            .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe((search) => this.searchChange.emit(search));
    }

    ngOnInit(): void {
        this.breakpointObserver
            .observe(DESKTOP_MQ)
            .pipe(takeUntil(this.destroy$))
            .subscribe((state) => {
                this.isDesktop = state.matches;
                if (this.isDesktop && this.showMenu) {
                    this._menuOpened = true;
                }
                this.changeDetector.markForCheck();
            });
    }

    ngAfterViewInit(): void {
        this.route.params.subscribe((params) => {
            if (params?.id?.length === 0) this.searchBox?.focusOnInput();
        });
    }

    @ViewChild(SearchBoxComponent)
    set searchBox(searchBox: SearchBoxComponent) {
        if (this._searchBox !== searchBox) {
            this._searchBox = searchBox;
            this.publishFromSearchBox();
        }
    }

    get searchBox(): SearchBoxComponent {
        return this._searchBox;
    }

    get menuOpened() {
        return this._menuOpened;
    }

    set menuOpened(value: boolean) {
        this.menuOpened$.emit((this._menuOpened = value));
    }

    closeMenu() {
        if (this.isDesktop) {
            return;
        }
        this.menuOpened = false;
    }

    toggleMenu() {
        if (this.isDesktop) {
            return;
        }
        this.menuOpened = !this.menuOpened;
    }

    onSidenavOpenedChange(opened: boolean) {
        if (this.isDesktop) {
            return;
        }
        this.menuOpened = opened;
    }

    onSwipeOpenMenu() {
        if (!this.isDesktop && this.showMenu) {
            this.menuOpened = true;
        }
    }

    onSearchChange(text: string) {
        this.searchSubject.next(text);
    }

    onSearchClear() {
        this.searchSubject.next('');
    }

    onGoBackClick(event: MouseEvent) {
        this.backClick.emit(event);
    }

    onLogoutClick() {
        this.logoutClick.emit();
    }

    goBack() {
        this.location.back();
    }

    resetMenu() {
        this.menuContainer.nativeElement.scrollTo({ top: 0 });
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
    }

    private publishFromSearchBox() {
        if (this.searchBox) {
            this.isSearchOpened$ = combineLatest([
                this.searchBox.isMobile$,
                this.searchBox.isSearchVisible$
            ]).pipe(map(([isMobile, isSearchVisible]) => !isMobile || !isSearchVisible));
            this.changeDetector.markForCheck();
        }
    }
}

export interface LayoutUser {
    name: string;
    identifier: string;
}
